import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, AlertCircle, Send, Star } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Park City Chiefs Logo
const PCC_LOGO = "https://customer-assets.emergentagent.com/job_tesa-pcc-build/artifacts/ahkhjrnt_image.png";

const BEHAVIOR_LABELS = {
  1: { label: 'Poor', color: '#dc2626', bg: '#fef2f2' },
  2: { label: 'Concerning', color: '#ea580c', bg: '#fff7ed' },
  3: { label: 'Needs Improvement', color: '#ca8a04', bg: '#fefce8' },
  4: { label: 'Good', color: '#16a34a', bg: '#f0fdf4' },
  5: { label: 'Excellent', color: '#059669', bg: '#ecfdf5' }
};

export default function TeacherFeedbackPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [academicStatus, setAcademicStatus] = useState('');
  const [currentAverage, setCurrentAverage] = useState('');
  const [behaviorScore, setBehaviorScore] = useState(0);
  const [comments, setComments] = useState('');
  const [teacherName, setTeacherName] = useState('');

  useEffect(() => {
    fetchFormData();
  }, [token]);

  const fetchFormData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/accountability/${token}`);
      
      if (res.status === 404) {
        setError('invalid');
        return;
      }
      if (res.status === 410) {
        const data = await res.json();
        if (data.detail?.includes('expired')) {
          setError('expired');
        } else {
          setError('already_submitted');
        }
        return;
      }
      if (!res.ok) {
        setError('error');
        return;
      }
      
      const data = await res.json();
      setFormData(data);
      setTeacherName(data.teacher_name || '');
    } catch (err) {
      setError('error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!academicStatus || behaviorScore === 0 || !teacherName.trim()) {
      alert('Please complete all required fields');
      return;
    }

    setSubmitting(true);

    try {
      const res = await fetch(`${API_URL}/api/accountability/${token}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          academic_status: academicStatus,
          current_average: currentAverage || null,
          behavior_score: behaviorScore,
          comments: comments || null,
          teacher_name: teacherName.trim()
        })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to submit');
      }

      setSubmitted(true);
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#c41e3a] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Error states
  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <img src={PCC_LOGO} alt="Park City Chiefs" className="h-16 mx-auto mb-6" />
          
          {error === 'invalid' && (
            <>
              <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h1 className="text-xl font-bold text-gray-900 mb-2">Invalid Link</h1>
              <p className="text-gray-600">This link is not valid. Please check the link in your email and try again.</p>
            </>
          )}
          
          {error === 'expired' && (
            <>
              <AlertCircle className="w-16 h-16 text-orange-500 mx-auto mb-4" />
              <h1 className="text-xl font-bold text-gray-900 mb-2">Link Expired</h1>
              <p className="text-gray-600">This link has expired. Please contact Park City Chiefs for a new link.</p>
            </>
          )}
          
          {error === 'already_submitted' && (
            <>
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h1 className="text-xl font-bold text-gray-900 mb-2">Already Submitted</h1>
              <p className="text-gray-600">Feedback for this student has already been submitted for this week. Thank you!</p>
            </>
          )}
          
          {error === 'error' && (
            <>
              <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h1 className="text-xl font-bold text-gray-900 mb-2">Something Went Wrong</h1>
              <p className="text-gray-600">Please try again later or contact Park City Chiefs for assistance.</p>
            </>
          )}
        </div>
      </div>
    );
  }

  // Success state
  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <img src={PCC_LOGO} alt="Park City Chiefs" className="h-16 mx-auto mb-6" />
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h1>
          <p className="text-gray-600 mb-6">
            Your feedback for <strong>{formData?.player_name}</strong> has been submitted successfully.
          </p>
          <p className="text-sm text-gray-500">
            Your input helps us ensure our student-athletes are succeeding both on and off the field.
          </p>
        </div>
      </div>
    );
  }

  // Form
  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-6">
          <div className="bg-[#0a0a0a] p-6 text-center">
            <img src={PCC_LOGO} alt="Park City Chiefs" className="h-14 mx-auto" />
          </div>
          <div className="bg-[#32425c] p-4 text-center">
            <h1 className="text-white font-bold text-lg">Weekly Student Check-in</h1>
            <p className="text-gray-300 text-sm mt-1">Week of {formData?.week_of}</p>
          </div>
          <div className="p-6 text-center border-b border-gray-100">
            <p className="text-gray-600 text-sm mb-2">Student</p>
            <h2 className="text-2xl font-bold text-[#32425c]">{formData?.player_name}</h2>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg p-6">
          {/* Academic Status */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Academic Standing <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setAcademicStatus('on_track')}
                className={`p-4 rounded-xl border-2 transition text-center ${
                  academicStatus === 'on_track'
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-700'
                }`}
              >
                <CheckCircle className={`w-6 h-6 mx-auto mb-2 ${academicStatus === 'on_track' ? 'text-green-500' : 'text-gray-400'}`} />
                <span className="font-medium">On Track</span>
                <p className="text-xs text-gray-500 mt-1">B average or higher</p>
              </button>
              <button
                type="button"
                onClick={() => setAcademicStatus('needs_improvement')}
                className={`p-4 rounded-xl border-2 transition text-center ${
                  academicStatus === 'needs_improvement'
                    ? 'border-orange-500 bg-orange-50 text-orange-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-700'
                }`}
              >
                <AlertCircle className={`w-6 h-6 mx-auto mb-2 ${academicStatus === 'needs_improvement' ? 'text-orange-500' : 'text-gray-400'}`} />
                <span className="font-medium">Needs Work</span>
                <p className="text-xs text-gray-500 mt-1">Below B average</p>
              </button>
            </div>
          </div>

          {/* Current Average (Optional) */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Current Average <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={currentAverage}
              onChange={(e) => setCurrentAverage(e.target.value)}
              placeholder="e.g., B+, 85%, etc."
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#32425c] focus:border-transparent"
            />
          </div>

          {/* Behavior Score */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Behavior Rating <span className="text-red-500">*</span>
            </label>
            <div className="flex justify-between gap-2">
              {[1, 2, 3, 4, 5].map((score) => (
                <button
                  key={score}
                  type="button"
                  onClick={() => setBehaviorScore(score)}
                  className={`flex-1 py-4 rounded-xl border-2 transition flex flex-col items-center ${
                    behaviorScore === score
                      ? `border-current`
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  style={behaviorScore === score ? {
                    borderColor: BEHAVIOR_LABELS[score].color,
                    backgroundColor: BEHAVIOR_LABELS[score].bg,
                    color: BEHAVIOR_LABELS[score].color
                  } : {}}
                >
                  <span className="text-2xl font-bold">{score}</span>
                  <Star className={`w-4 h-4 mt-1 ${behaviorScore === score ? 'fill-current' : 'text-gray-300'}`} />
                </button>
              ))}
            </div>
            {behaviorScore > 0 && (
              <p className="text-center mt-3 font-medium" style={{ color: BEHAVIOR_LABELS[behaviorScore].color }}>
                {BEHAVIOR_LABELS[behaviorScore].label}
              </p>
            )}
            <div className="flex justify-between text-xs text-gray-400 mt-2 px-2">
              <span>Poor</span>
              <span>Excellent</span>
            </div>
          </div>

          {/* Comments */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Comments <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              rows={3}
              placeholder="Any additional feedback about this student..."
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#32425c] focus:border-transparent resize-none"
            />
          </div>

          {/* Teacher Name */}
          <div className="mb-8">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Your Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={teacherName}
              onChange={(e) => setTeacherName(e.target.value)}
              placeholder="Enter your name"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#32425c] focus:border-transparent"
              required
            />
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting || !academicStatus || behaviorScore === 0 || !teacherName.trim()}
            className="w-full py-4 bg-[#c41e3a] text-white rounded-xl font-semibold text-lg hover:bg-[#a01830] transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                Submit Feedback
              </>
            )}
          </button>

          <p className="text-center text-xs text-gray-500 mt-4">
            Thank you for supporting our student-athletes!
          </p>
        </form>
      </div>
    </div>
  );
}
